<template>
  <main id="live">
    <AviaPlayer
      v-if="!loading && resources.current"
    ></AviaPlayer>
    <Tray
      v-if="!loading && !isPlayerOnly"
      v-for="playlist in playlists"
      :key="playlist.category"
      :tray="playlist.category"
      :playlist="playlist"
    >
    </Tray>
    <Taboola v-if="this.trayUpdated && !isPlayerOnly"></Taboola>
  </main>
</template>

<script>
import { mapState, mapGetters, mapMutations, mapActions } from 'vuex'
import AviaPlayer from '../components/AviaPlayer'

export default {
  name: 'Live',
  components: {
    AviaPlayer,
    Tray: () => import(/* webpackChunkName: "tray", webpackPreload: true */'../components/Tray'),
    Taboola: () => import(/* webpackChunkName: "taboola" */ '../components/Taboola')
  },
  mounted () {
    let slug = null
    let routeType = this.$route.matched[0].path

    if (this.$route.params.hasOwnProperty('slug')) {
      slug = this.$route.params.slug
    }

    // we are not passing the 'tray' value because we do not care on initial load
    this.appInit({ slug, routeType })

    if (this.resources.current !== null) {
      this.updateAllTags(this.resources.current)
    }
    import('../assets/utils').then(utils => {
      utils.default.debounce(this, () => {
        this.setTrayUpdated(true)
      }, 5000)
    })
  },

  computed: {
    ...mapState([
      'resources',
      'loading',
      'startingRoute',
      'isPlayerOnly',
      'trayUpdated'
    ]),
    ...mapGetters([
      'playlists',
      'isDrupal',
      'wasVODPage'
    ])
  },
  methods: {
    ...mapMutations([
      'setResources',
      'setCurrentRoute',
      'setTrayUpdated'
    ]),
    ...mapActions([
      'appInit'
    ]),
    /**
     * More setting unique keys everywhere
     */
    setUniqueKey () {
      let key = ''
      import('../assets/utils').then(utils => {
        key = utils.default.generateUniqueKey()
      })
      return key
    },

    /**
     * Update meta tags
     */
    updateAllTags (resource) {
      // update thumbnail dimensions
      let newImageSize = ''
      import('../assets/utils').then(utils => {
        newImageSize = utils.default.newImageSize(resource.ThumbnailURL)
      })

      let ogDescription = resource.Subhead ? resource.Subhead : resource.StoryHead

      // TODO - Use a variable for the the site name.
      document.querySelector('meta[name="title"]') ? this.updateTagWithAttributes('meta', 'name', 'title', 'content', resource.StoryHead + ' | Inside Edition') : this.createNewTagWithAttributes('meta', 'name', 'title', 'content', resource.StoryHead)
      document.querySelector('meta[name="description"]') ? this.updateTagWithAttributes('meta', 'name', 'description', 'content', typeof resource.Description === 'undefined' ? resource.StoryHead : this.stripHtmlTags(resource.Description)) : this.createNewTagWithAttributes('meta', 'name', 'description', 'content', typeof resource.Description === 'undefined' ? resource.StoryHead : this.stripHtmlTags(resource.Description))
      document.querySelector('meta[name="twitter:title"]') ? this.updateTagWithAttributes('meta', 'name', 'twitter:title', 'content', resource.StoryHead) : this.createNewTagWithAttributes('meta', 'name', 'twitter:title', 'content', resource.StoryHead)
      document.querySelector('meta[name="twitter:description"]') ? this.updateTagWithAttributes('meta', 'name', 'twitter:description', 'content', typeof resource.Description === 'undefined' ? resource.StoryHead : this.stripHtmlTags(resource.Description)) : this.createNewTagWithAttributes('meta', 'name', 'twitter:description', 'content', typeof resource.Description === 'undefined' ? resource.StoryHead : this.stripHtmlTags(resource.Description))
      document.querySelector('meta[name="twitter:url"]') ? this.updateTagWithAttributes('meta', 'name', 'twitter:url', 'content', this.getResourcePageUrl(resource)) : this.createNewTagWithAttributes('meta', 'name', 'twitter:url', 'content', this.getResourcePageUrl(resource))
      document.querySelector('meta[name="twitter:image"]') ? this.updateTagWithAttributes('meta', 'name', 'twitter:image', 'content', newImageSize) : this.createNewTagWithAttributes('meta', 'name', 'twitter:image', 'content', newImageSize)
      document.querySelector('meta[property="og:image"]') ? this.updateTagWithAttributes('meta', 'property', 'og:image', 'content', newImageSize) : this.createNewTagWithAttributes('meta', 'property', 'og:image', 'content', newImageSize)
      document.querySelector('meta[property="og:video"]') ? this.updateTagWithAttributes('meta', 'property', 'og:video', 'content', resource.AssetURL) : this.createNewTagWithAttributes('meta', 'property', 'og:video', 'content', resource.AssetURL)
      document.querySelector('meta[property="og:url"]') ? this.updateTagWithAttributes('meta', 'property', 'og:url', 'content', this.getResourcePageUrl(resource)) : this.createNewTagWithAttributes('meta', 'property', 'og:url', 'content', this.getResourcePageUrl(resource))
      document.querySelector('meta[property="og:title"]') ? this.updateTagWithAttributes('meta', 'property', 'og:title', 'content', resource.StoryHead) : this.createNewTagWithAttributes('meta', 'property', 'og:title', 'content', resource.StoryHead)
      document.querySelector('meta[property="og:description"]') ? this.updateTagWithAttributes('meta', 'property', 'og:description', 'content', ogDescription) : this.createNewTagWithAttributes('meta', 'property', 'og:description', 'content', ogDescription)
      document.querySelector('link[rel="canonical"]') ? this.updateTagWithAttributes('link', 'rel', 'canonical', 'href', this.getResourcePageUrl(resource)) : this.createNewTagWithAttributes('link', 'rel', 'canonical', 'href', this.getResourcePageUrl(resource))
      document.querySelector('link[rel="image_src"]') ? this.updateTagWithAttributes('link', 'rel', 'image_src', 'href', newImageSize) : this.createNewTagWithAttributes('link', 'rel', 'image_src', 'href', newImageSize)
      document.title = resource.StoryHead
      this.prepareVideoObjectData(resource)
    },

    /**
     * Removes HTML tags from string
     */
    stripHtmlTags (strInput) {
      return strInput.replace(/<\/?[^>]+(>|$)/g, '').replace(/\r?\n|\r/g, '')
    },
    /**
     * Create new tag with attributes
     */
    createNewTagWithAttributes (type, att1, val1, att2, val2) {
      let tag = document.createElement(type)
      tag.setAttribute(att1, val1)
      tag.setAttribute(att2, val2)
      document.getElementsByTagName('head')[0].appendChild(tag)
    },
    /**
     * Update tag with attributes
     */
    updateTagWithAttributes (type, att1, val1, att2, val2) {
      let tag = document.querySelector(type + '[' + att1 + '="' + val1 + '"]')
      if (tag) {
        tag.setAttribute(att1, val1)
        tag.setAttribute(att2, val2)
      }
    },
    /**
     * Returns page url
     *
     * @returns {string}
     */
    getResourcePageUrl (resource) {
      return window.location.origin + resource.StoryPath + resource.StorySlug
    },
    /**
     * Returns thumbnail size
     *
     * @returns {string}
     */
    getThumbnailSize (resource) {
      // https://www.etonline.com/sites/default/files/styles/300x169/
      let url = resource.ThumbnailURL
      var thumbsize = url.match('/styles/(.+?)/')
      return thumbsize ? thumbsize[1] : '320x169'
    },

    /**
     * Gets embed URL
     *
     * @returns {string}
     */
    getEmbedUrl (resource) {
      let BCvID = JSON.stringify(resource.StoryId)
      return 'https://img.etonline.com/uvp/player/ie/videoplayer.html?video_id=' + BCvID
    },

    /**
     * Prepares linked data
     *
     * @returns {object}
     */
    prepareVideoObjectData (resource) {
      let isTimeCode = /^\d{2}:\d{2}:\d{2}$/

      let duration = ''
      if (isTimeCode.test(resource.Duration)) {
        let durationParts = resource.Duration.split(':')
        duration = 'PT' + parseInt(durationParts[0]) + 'H' + parseInt(durationParts[1]) + 'M' + parseInt(durationParts[2]) + 'S'
      }

      let linkedData =
      {
        '@context': 'https://schema.org',
        '@type': 'VideoObject',
        'mainEntityOfPage': [
          {
            '@type': 'WebPage',
            '@id': this.getResourcePageUrl(resource)
          }
        ],
        'name': resource.StoryHead,
        'description': typeof (resource.Description) === 'undefined' ? resource.StoryHead : resource.Description,
        'thumbnail':
          [{
            '@context': 'https://schema.org',
            '@type': 'ImageObject',
            'height': this.getThumbnailSize(resource).split('x')[1],
            'width': this.getThumbnailSize(resource).split('x')[0],
            'url': resource.ThumbnailURL
          }],
        'thumbnailUrl': resource.ThumbnailURL,
        'uploadDate': typeof (resource.PublishDate) === 'undefined' ? (new Date(resource.StartTime * 1000)).toISOString() : resource.PublishDate,
        'contentUrl': resource.AssetURL,
        'publisher':
        [{
          '@context': 'https://schema.org',
          '@id': 'https://www.insideedition.com/',
          '@type': 'Organization',
          'name': 'Inside Edition',
          'sameAs':
              ['https://www.insideedition.com/',
                'https://www.facebook.com/Inside.Edition',
                'https://www.instagram.com/insideedition/',
                'https://twitter.com/insideedition',
                'http://www.youtube.com/user/cbstvdinsideedition',
                'https://en.wikipedia.org/wiki/Inside_Edition'
              ],
          'logo':
            [{
              '@type': 'ImageObject',
              'url': 'https://www.insideedition.com/themes/custom/iebs/images/logo-nav-default.png',
              'width': 198,
              'height': 30
            }],
          'url': 'https://www.insideedition.com/'
        }],
        'duration': duration,
        'embedUrl': this.getEmbedUrl(resource)
      }
      let tag = null
      let scriptTags = document.querySelectorAll('script[type="application/ld+json"]')
      scriptTags.forEach(function (stag) {
        if (stag.text.indexOf('VideoObject') !== -1) {
          tag = stag
          tag.text = JSON.stringify(linkedData)
        }
      })
      if (tag === null) {
        tag = document.createElement('script')
        tag.type = 'application/ld+json'
        tag.text = JSON.stringify(linkedData)
        document.getElementsByTagName('head')[0].appendChild(tag)
      }
    }
  },
  watch: {
    $route (to, from) {
      let slug = null
      let tray = null
      let routeType = this.$route.matched[0].path

      // for route checks throughout the app
      this.setCurrentRoute(routeType)

      if (to.params.hasOwnProperty('slug')) {
        slug = to.params.slug
      }
      if (to.params.hasOwnProperty('tray')) {
        tray = to.params.tray
      }
      this.setResources({
        slug: slug,
        playlists: this.playlists,
        tray: tray
      })
    },
    resources (val, oldVal) {
      this.updateAllTags(val.current)
    }
  }
}
</script>
